import { useMutation } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { api } from '../api.js';
import { Button, Card, ErrorBox } from '../ui.js';

/**
 * §7: Primärfrage ist die 7-stufige thermische Empfindungsskala (ASHRAE / ISO 10551),
 * beantwortbar mit EINEM Tap. Alles Weitere ist optional und eingeklappt — wer abends
 * um halb neun drei Pflichtfelder ausfüllen muss, bewertet ab Woche zwei nichts mehr.
 */
const SCALE = [
  { value: -3, label: 'viel zu kalt' },
  { value: -2, label: 'zu kalt' },
  { value: -1, label: 'etwas zu kalt' },
  { value: 0, label: 'genau richtig' },
  { value: 1, label: 'etwas zu warm' },
  { value: 2, label: 'zu warm' },
  { value: 3, label: 'viel zu warm' },
];

const BLAME = [
  { value: 'top', label: 'Oberteil' },
  { value: 'mid', label: 'Zwischenschicht' },
  { value: 'outer', label: 'Jacke/Mantel' },
  { value: 'bottom', label: 'Hose' },
  { value: 'footwear', label: 'Schuhe' },
];

export function FeedbackSheet({ wearLogId, onClose }: {
  wearLogId: string;
  onClose: () => void;
}): ReactNode {
  const [expanded, setExpanded] = useState(false);
  const [comfort, setComfort] = useState<number | null>(null);
  const [look, setLook] = useState<number | null>(null);
  const [blame, setBlame] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const save = useMutation({
    mutationFn: (thermalVote: number) =>
      api.setFeedback(wearLogId, {
        thermalVote,
        comfort,
        lookConfidence: look,
        blameLayer: blame,
        note: note === '' ? null : note,
      }),
    onSuccess: onClose,
  });

  return (
    <Card className="mb-4 ring-[var(--color-accent)]/30">
      <p className="text-sm font-medium">Wie war es temperaturmäßig?</p>
      <div className="mt-3 space-y-2">
        {SCALE.map((s) => (
          <button
            key={s.value}
            onClick={() => save.mutate(s.value)}
            disabled={save.isPending}
            className={`press flex w-full items-center justify-between rounded-xl px-4 py-3 text-sm ${
              s.value === 0 ? 'bg-emerald-500/15 text-emerald-200' : 'bg-white/8 text-white/85'
            }`}
          >
            <span>{s.label}</span>
            <span className="text-xs text-white/45">
              {s.value > 0 ? `+${s.value}` : s.value}
            </span>
          </button>
        ))}
      </div>

      <button
        onClick={() => setExpanded(!expanded)}
        className="mt-3 w-full text-left text-xs text-white/55"
      >
        {expanded ? '− Details ausblenden' : '+ Mehr Details (optional)'}
      </button>

      {expanded && (
        <div className="mt-3 space-y-4 border-t border-white/10 pt-3">
          <Rating label="Komfort" value={comfort} onChange={setComfort} />
          <Rating label="Optik" value={look} onChange={setLook} />
          <div>
            <p className="mb-2 text-xs text-white/55">
              Woran lag es? Das verkürzt die Lernkurve erheblich.
            </p>
            <div className="flex flex-wrap gap-2">
              {BLAME.map((b) => (
                <button
                  key={b.value}
                  onClick={() => setBlame(blame === b.value ? null : b.value)}
                  className={`rounded-full px-3 py-2 text-xs ${
                    blame === b.value ? 'bg-white text-black' : 'bg-white/8 text-white/75'
                  }`}
                >
                  {b.label}
                </button>
              ))}
            </div>
          </div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Notiz (optional)"
            rows={2}
            className="w-full rounded-xl bg-white/6 px-3 py-2 text-sm ring-1 ring-white/10 outline-none focus:ring-sky-500"
          />
        </div>
      )}

      <ErrorBox error={save.error} />
      <Button variant="ghost" className="mt-3 w-full" onClick={onClose}>
        Später
      </Button>
    </Card>
  );
}

function Rating({ label, value, onChange }: {
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
}): ReactNode {
  return (
    <div>
      <p className="mb-2 text-xs text-white/55">{label}</p>
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            onClick={() => onChange(value === n ? null : n)}
            className={`flex-1 rounded-lg py-2 text-sm ${
              value === n ? 'bg-white text-black' : 'bg-white/8 text-white/75'
            }`}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}
